import { ReactElement, useMemo } from 'react';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useTranslation } from 'react-i18next';
import styles from './InteractionSummaryVI.module.scss';
import AssetInfo, { AssetInfoRow } from '@customer/components/asset-data/asset-info/AssetInfo';
import ObjectComparisonTable from '@customer/components/tables/object-comparison-table/ObjectComparisonTable';
import GoodInvalidRenderer from '@customer/components/tables/cells/good-invalid/GoodInvalidRenderer';
import { useDistanceText } from '@customer/components/tables/formatters/numberFormatters';
import { AssetVIModel } from '@customer/store/apis/interactionsApi';

interface InteractionSummaryVIProps {
  interaction?: AssetVIModel;
  verticalPadding?: boolean;
}

export const InteractionSummaryVI = ({
  interaction,
  verticalPadding = false,
}: InteractionSummaryVIProps): ReactElement => {
  const { t } = useTranslation();
  const { showDurationFromSeconds } = useLocaleFormatter();
  const getDistanceText = useDistanceText();

  const {
    dur: duration,
    dist: distance,
    rn: ruleName,
    act: active,
    lo,
    ro,
    svfix,
    evfix,
  } = interaction ?? {};

  const infoRows: AssetInfoRow[] = useMemo(
    () => [
      {
        label: t('GpsFix'),
        value: <GoodInvalidRenderer value={svfix} endFixValid={evfix} />,
      },
      {
        label: t('Duration'),
        value: active
          ? t('Active')
          : typeof duration === 'number'
            ? showDurationFromSeconds(duration)
            : '',
      },
      {
        label: t('Distance'),
        value: typeof distance === 'number' ? getDistanceText(distance) : '',
      },
      {
        label: t('Rule'),
        value: ruleName ?? '',
      },
    ],
    [
      t,
      active,
      duration,
      distance,
      ruleName,
      svfix,
      evfix,
      showDurationFromSeconds,
      getDistanceText,
    ],
  );

  return (
    <div
      className={`${styles.container} ${verticalPadding && styles['container--verticalPadding']}`}
    >
      <AssetInfo rows={infoRows} verticalPadding={false} horizontalPadding={true} />

      <ObjectComparisonTable sliceKey={'LoRoComparison'} lo={lo} ro={ro} />
    </div>
  );
};

export default InteractionSummaryVI;
